import React, { useMemo } from 'react';
import { S3Object } from '../types';

interface BulkActionBarProps {
  selectedObjects: S3Object[];
  onDownload: () => Promise<void>;
  onDelete: () => Promise<void>;
  onRestore: () => Promise<void>;
  onPermanentDelete: () => Promise<void>;
  onShare?: () => void;
  onUnshare?: () => void;
  onCopy?: () => void;
  onMove?: () => void;
  onClearSelection: () => void;
  isProcessing: boolean;
  isLyveBucket?: boolean;
  sharedObjectKeys?: string[];
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedObjects,
  onDownload,
  onDelete,
  onRestore,
  onPermanentDelete,
  onShare,
  onUnshare,
  onCopy,
  onMove,
  onClearSelection,
  isProcessing,
  isLyveBucket = false,
  sharedObjectKeys = []
}) => {
  // Compute active and deleted objects
  const activeObjects = useMemo(
    () => selectedObjects.filter(obj => !obj.isDeleted),
    [selectedObjects]
  );

  const deletedObjects = useMemo(
    () => selectedObjects.filter(obj => obj.isDeleted),
    [selectedObjects]
  );

  // Compute shared objects among selected
  const sharedObjects = useMemo(
    () => activeObjects.filter(obj => sharedObjectKeys.includes(obj.key)),
    [activeObjects, sharedObjectKeys]
  );

  const hasActiveObjects = activeObjects.length > 0;
  const hasDeletedObjects = deletedObjects.length > 0;
  const hasSharedObjects = sharedObjects.length > 0;

  // Don't render if no items selected
  if (selectedObjects.length === 0) {
    return null;
  }

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: '#f9fafb',
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem'
    }}>
      {/* Selection count */}
      <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>
        {selectedObjects.length} item{selectedObjects.length !== 1 ? 's' : ''} selected
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {/* Share button - only for Lyve buckets with active objects selected */}
        {hasActiveObjects && isLyveBucket && onShare && (
          <button
            onClick={onShare}
            className="btn btn-primary btn-small"
            disabled={isProcessing}
            style={{ 
              fontSize: '0.875rem',
              backgroundColor: '#667eea',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            🔗 Share
          </button>
        )}

        {/* Unshare button - only for Lyve buckets with shared objects selected */}
        {hasSharedObjects && isLyveBucket && onUnshare && (
          <button
            onClick={onUnshare}
            className="btn btn-warning btn-small"
            disabled={isProcessing}
            style={{ 
              fontSize: '0.875rem',
              backgroundColor: '#f59e0b',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
            title={`Unshare ${sharedObjects.length} object(s)`}
          >
            🔓 Unshare ({sharedObjects.length})
          </button>
        )}

        {/* Download button - only for active objects */}
        {hasActiveObjects && (
          <button
            onClick={onDownload}
            className="btn btn-primary btn-small"
            disabled={isProcessing}
            style={{ fontSize: '0.875rem' }}
          >
            Download Selected
          </button>
        )}

        {/* Copy button */}
        {hasActiveObjects && onCopy && (
          <button
            onClick={onCopy}
            className="btn btn-primary btn-small"
            disabled={isProcessing}
            style={{ fontSize: '0.875rem', backgroundColor: '#0891b2' }}
          >
            📋 Copy to…
          </button>
        )}

        {/* Move button */}
        {hasActiveObjects && onMove && (
          <button
            onClick={onMove}
            className="btn btn-primary btn-small"
            disabled={isProcessing}
            style={{ fontSize: '0.875rem', backgroundColor: '#7c3aed' }}
          >
            ✂️ Move to…
          </button>
        )}

        {/* Delete button - only for active objects */}
        {hasActiveObjects && (
          <button
            onClick={onDelete}
            className="btn btn-danger btn-small"
            disabled={isProcessing}
            style={{ fontSize: '0.875rem' }}
          >
            Delete Selected
          </button>
        )}

        {/* Restore button - only for deleted objects */}
        {hasDeletedObjects && (
          <button
            onClick={onRestore}
            className="btn btn-success btn-small"
            disabled={isProcessing}
            style={{ fontSize: '0.875rem' }}
          >
            Restore Selected
          </button>
        )}

        {/* Permanently Delete button - only for deleted objects */}
        {hasDeletedObjects && (
          <button
            onClick={onPermanentDelete}
            className="btn btn-danger btn-small"
            disabled={isProcessing}
            style={{ 
              fontSize: '0.875rem',
              backgroundColor: '#8B0000'
            }}
          >
            Permanently Delete Selected
          </button>
        )}

        {/* Clear selection button */}
        <button
          onClick={onClearSelection}
          className="btn btn-secondary btn-small"
          disabled={isProcessing}
          style={{ fontSize: '0.875rem' }}
        >
          Clear Selection
        </button>
      </div>
    </div>
  );
};

export default BulkActionBar;
